import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({ user:null, profile:null, loading:true, signIn:()=>{}, signUp:()=>{}, signOut:()=>{}, refreshProfile:()=>{} })

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 5000)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else { setProfile(null); setLoading(false) }
    })
    return () => { subscription.unsubscribe(); clearTimeout(timeout) }
  }, [])

  async function fetchProfile(userId) {
    try {
      const { data } = await supabase.from('profiles').select('*').eq('id', userId).single()
      if (data) {
        // ✅ Ensure jsonb columns are proper types — never raw objects that break React!!
        data.address_book   = Array.isArray(data.address_book)   ? data.address_book   : []
        data.office_prefs   = data.office_prefs   && typeof data.office_prefs   === 'object' ? data.office_prefs   : {}
        data.default_fee_override = data.default_fee_override && typeof data.default_fee_override === 'object' ? data.default_fee_override : {}
      }
      setProfile(data)
    } catch (err) {
      console.error('Profile fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  async function signUp(email, password, meta) {
    const { data, error } = await supabase.auth.signUp({ email, password, options: { data: meta } })
    if (error) throw error

    const userId = data.user?.id
    if (userId) {
      // ✅ Only individuals are auto-approved!!
      // Notaries and clients require manual admin approval!!
      const isApproved = meta.role === 'individual'

      await supabase.from('profiles').upsert({
        id:               userId,
        email,
        full_name:        meta.full_name,
        phone:            meta.phone,
        role:             meta.role || 'individual',
        is_approved:      isApproved,
        company_name:     meta.company_name     || null,
        address:          meta.address          || null,
        city:             meta.city             || null,
        state:            meta.state            || null,
        zip:              meta.zip              || null,
        business_address: meta.business_address || null,
        business_city:    meta.business_city    || null,
        business_state:   meta.business_state   || null,
        business_zip:     meta.business_zip     || null,
      })

      setProfile({
        id:               userId,
        email,
        full_name:        meta.full_name,
        phone:            meta.phone,
        role:             meta.role || 'individual',
        is_approved:      isApproved,
        company_name:     meta.company_name     || null,
        address:          meta.address          || null,
        city:             meta.city             || null,
        state:            meta.state            || null,
        zip:              meta.zip              || null,
        business_address: meta.business_address || null,
        business_city:    meta.business_city    || null,
        business_state:   meta.business_state   || null,
        business_zip:     meta.business_zip     || null,
      })
      setLoading(false)
    }
    return data
  }

  async function signOut() {
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
  }

  async function refreshProfile() {
    if (user) await fetchProfile(user.id)
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signUp, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
